import { useState, useEffect } from 'react';
import subscriptionService from '../services/subscriptionService';

export const useSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const { data, error } = await subscriptionService.getAllSubscriptions();
      if (error) {
        setError(error);
      } else {
        setSubscriptions(data);
        setError(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async (subscriptionData) => {
    try {
      const { data, error } = await subscriptionService.createSubscription(subscriptionData);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setSubscriptions(prev => [data, ...prev]);
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const updateSubscription = async (id, subscriptionData) => {
    try {
      const { data, error } = await subscriptionService.updateSubscription(id, subscriptionData);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setSubscriptions(prev => prev.map(sub => sub.id === id ? data : sub));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const cancelSubscription = async (id, reason) => {
    try {
      const { data, error } = await subscriptionService.cancelSubscription(id, reason);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setSubscriptions(prev => prev.map(sub => sub.id === id ? data : sub));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const pauseSubscription = async (id, reason) => {
    try {
      const { data, error } = await subscriptionService.pauseSubscription(id, reason);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setSubscriptions(prev => prev.map(sub => sub.id === id ? data : sub));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const resumeSubscription = async (id) => {
    try {
      const { data, error } = await subscriptionService.resumeSubscription(id);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setSubscriptions(prev => prev.map(sub => sub.id === id ? data : sub));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const upgradeSubscription = async (id, newPlanId, prorated = true) => {
    try {
      const { data, error } = await subscriptionService.upgradeSubscription(id, newPlanId, prorated);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setSubscriptions(prev => prev.map(sub => sub.id === id ? data : sub));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const processRenewal = async (id) => {
    try {
      const { data, error } = await subscriptionService.processRenewal(id);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setSubscriptions(prev => prev.map(sub => sub.id === id ? data.subscription : sub));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  const convertTrialToSubscription = async (id, planId = null) => {
    try {
      const { data, error } = await subscriptionService.convertTrialToSubscription(id, planId);
      if (error) {
        setError(error);
        return { success: false, error };
      }
      
      setSubscriptions(prev => prev.map(sub => sub.id === id ? data : sub));
      return { success: true, data };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  return {
    subscriptions,
    loading,
    error,
    refetch: fetchSubscriptions,
    createSubscription,
    updateSubscription,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
    upgradeSubscription,
    processRenewal,
    convertTrialToSubscription
  };
};

export const useSubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const { data } = await subscriptionService.getAllPlans();
        setPlans(data);
      } catch (error) {
        console.error('Error fetching subscription plans:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return { plans, loading };
};

export const useSubscriptionStatistics = () => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await subscriptionService.getSubscriptionStatistics();
        setStats(data);
      } catch (error) {
        console.error('Error fetching subscription statistics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  return { stats, loading };
};

export const useExpiringTrials = (daysAhead = 3) => {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrials = async () => {
      try {
        const { data } = await subscriptionService.getExpiringTrials(daysAhead);
        setTrials(data);
      } catch (error) {
        console.error('Error fetching expiring trials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrials();
  }, [daysAhead]);

  return { trials, loading };
};

export const useUpcomingRenewals = (daysAhead = 7) => {
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRenewals = async () => {
      try {
        const { data } = await subscriptionService.getUpcomingRenewals(daysAhead);
        setRenewals(data);
      } catch (error) {
        console.error('Error fetching upcoming renewals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRenewals();
  }, [daysAhead]);

  return { renewals, loading };
};